import { useAuth } from '../contexts/AuthContext';
import { useCallback, useMemo, useRef } from 'react';

const BASE_URL = '/api';

export function useApi() {
  const { token, logout } = useAuth();

  // Use a ref so request() always reads the latest token/logout
  // without needing them as dependencies (which would cause new object refs)
  const tokenRef = useRef(token);
  const logoutRef = useRef(logout);
  tokenRef.current = token;
  logoutRef.current = logout;

  const request = useCallback(async (method, path, body = null) => {
    const headers = { 'Content-Type': 'application/json' };
    if (tokenRef.current) headers['Authorization'] = `Bearer ${tokenRef.current}`;

    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });

    if (res.status === 401) {
      logoutRef.current();
      throw new Error('Session expired');
    }

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  }, []); // stable — never recreated

  // useMemo ensures the returned object is also stable across renders
  return useMemo(() => ({
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    del: (path) => request('DELETE', path),
  }), [request]);
}
